<template>
  <div class="page-container">
    <el-card shadow="never">
      <!-- 搜索区 -->
      <div class="search-bar">
        <el-select v-model="query.platform" placeholder="平台" clearable style="width: 110px">
          <el-option label="Windows" value="win" />
          <el-option label="macOS" value="mac" />
          <el-option label="Linux" value="linux" />
        </el-select>
        <el-select v-model="query.arch" placeholder="架构" clearable style="width: 110px">
          <el-option label="x64" value="x64" />
          <el-option label="arm64" value="arm64" />
          <el-option label="ia32" value="ia32" />
        </el-select>
        <el-select v-model="query.status" placeholder="发布状态" clearable style="width: 110px">
          <el-option label="已发布" :value="1" />
          <el-option label="已禁用" :value="0" />
        </el-select>
        <el-button type="primary" size="mini" icon="el-icon-search" @click="handleSearch">查询</el-button>
        <el-button size="mini" icon="el-icon-refresh" @click="handleReset">重置</el-button>
      </div>

      <!-- 操作按钮 -->
      <div class="action-bar">
        <el-button
          v-permission="['app:version:create']"
          type="success"
          size="mini"
          icon="el-icon-plus"
          @click="handleCreate"
        >发布新版本</el-button>
      </div>

      <!-- 表格 -->
      <el-table :data="tableData" v-loading="loading" border stripe style="width: 100%">
        <el-table-column prop="id" label="ID" width="65" align="center" />
        <el-table-column label="版本号" width="100" align="center">
          <template slot-scope="{ row }">
            <span class="version-text">v{{ row.version }}</span>
          </template>
        </el-table-column>
        <el-table-column label="平台" width="95" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="platformTagType(row.platform)" size="small">
              {{ platformLabel(row.platform) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="架构" width="80" align="center">
          <template slot-scope="{ row }">
            <el-tag type="info" size="small">{{ row.arch }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="文件名" min-width="200" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <i class="el-icon-document" style="margin-right:4px;color:#909399" />
            {{ row.file_name }}
          </template>
        </el-table-column>
        <el-table-column label="文件大小" width="105" align="right">
          <template slot-scope="{ row }">
            <span class="size-text">{{ formatSize(row.file_size) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="MD5" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tooltip :content="row.file_md5" placement="top">
              <span class="md5-short">{{ row.file_md5 ? row.file_md5.slice(0, 8) + '…' : '-' }}</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column label="更新日志" min-width="180" show-overflow-tooltip>
          <template slot-scope="{ row }">
            {{ row.release_note || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="强制更新" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.is_force === 1 ? 'danger' : 'info'" size="small">
              {{ row.is_force === 1 ? '强制' : '否' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'info'" size="small">
              {{ row.status === 1 ? '已发布' : '已禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="发布时间" width="160" />
        <el-table-column label="操作" width="140" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button
              v-permission="['app:version:update']"
              type="primary"
              size="mini"
              icon="el-icon-edit"
              @click="handleEdit(row)"
            >编辑</el-button>
            <el-button
              v-permission="['app:version:delete']"
              type="danger"
              size="mini"
              icon="el-icon-delete"
              @click="handleDelete(row)"
            >删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        :current-page="query.page"
        :page-sizes="[10, 15, 20, 50]"
        :page-size="query.pageSize"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
        style="margin-top: 16px; text-align: right"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </el-card>

    <!-- 新增 / 编辑弹窗 -->
    <el-dialog
      :title="dialog.title"
      :visible.sync="dialog.visible"
      width="580px"
      @close="resetDialog"
    >
      <el-form
        ref="versionForm"
        :model="form"
        :rules="rules"
        label-width="100px"
        size="small"
      >
        <!-- 文件上传区（仅新增时显示） -->
        <el-form-item v-if="!form.id" label="安装包" prop="file_url">
          <div v-if="!uploadedFile.file_url" class="upload-area">
            <el-upload
              drag
              action="#"
              :http-request="handleFileUpload"
              :before-upload="beforeUpload"
              :show-file-list="false"
              :disabled="uploading"
            >
              <i class="el-icon-upload" />
              <div class="el-upload__text">
                将安装包拖到此处，或<em>点击上传</em>
              </div>
              <div class="el-upload__tip" slot="tip">
                支持 exe / dmg / pkg / deb / rpm / AppImage / zip 等格式
              </div>
            </el-upload>
            <div v-if="uploading" class="upload-progress">
              <i class="el-icon-loading" /> 正在上传…
            </div>
          </div>
          <div v-else class="file-info-card">
            <div class="file-info-row">
              <i class="el-icon-document-checked file-ok-icon" />
              <span class="file-name">{{ uploadedFile.file_name }}</span>
              <el-button type="text" size="mini" class="re-upload-btn" @click="clearUpload">重新上传</el-button>
            </div>
            <div class="file-meta">
              <span>大小：{{ formatSize(uploadedFile.file_size) }}</span>
              <span style="margin-left:16px">MD5：{{ uploadedFile.file_md5 }}</span>
            </div>
          </div>
        </el-form-item>

        <el-form-item label="版本号" prop="version">
          <el-input v-model="form.version" placeholder="如：1.2.0（x.y.z 格式）" style="width: 200px" />
        </el-form-item>

        <el-form-item label="目标平台" prop="platform">
          <el-radio-group v-model="form.platform" :disabled="!!form.id">
            <el-radio label="win">Windows</el-radio>
            <el-radio label="mac">macOS</el-radio>
            <el-radio label="linux">Linux</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="CPU 架构" prop="arch">
          <el-radio-group v-model="form.arch" :disabled="!!form.id">
            <el-radio label="x64">x64</el-radio>
            <el-radio label="arm64">arm64</el-radio>
            <el-radio label="ia32">ia32</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="更新日志">
          <el-input
            v-model="form.release_note"
            type="textarea"
            :rows="3"
            placeholder="请输入本次版本的更新说明（可选）"
          />
        </el-form-item>

        <el-form-item label="强制更新">
          <el-radio-group v-model="form.is_force">
            <el-radio :label="0">否</el-radio>
            <el-radio :label="1">是（强制）</el-radio>
          </el-radio-group>
          <div class="form-tip">开启后客户端将强制升级，无法跳过</div>
        </el-form-item>

        <el-form-item label="发布状态">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">立即发布</el-radio>
            <el-radio :label="0">暂不发布</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>

      <div slot="footer">
        <el-button size="mini" @click="dialog.visible = false">取消</el-button>
        <el-button type="primary" size="mini" :loading="dialog.loading" @click="handleSubmit">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getAppVersionList,
  uploadAppVersionFile,
  createAppVersion,
  updateAppVersion,
  deleteAppVersion
} from '@/api/app-version'

const VERSION_REG = /^\d+\.\d+\.\d+$/

const defaultForm = () => ({
  id: null,
  version: '',
  platform: 'win',
  arch: 'x64',
  file_url: '',
  release_note: '',
  is_force: 0,
  status: 1
})

export default {
  name: 'AppVersion',
  data() {
    return {
      loading: false,
      tableData: [],
      total: 0,
      query: {
        platform: '',
        arch: '',
        status: '',
        page: 1,
        pageSize: 15
      },
      dialog: { visible: false, title: '', loading: false },
      form: defaultForm(),
      rules: {
        file_url: [{ required: true, message: '请先上传安装包文件', trigger: 'change' }],
        version:  [
          { required: true, message: '请输入版本号', trigger: 'blur' },
          { pattern: VERSION_REG, message: '版本号格式应为 x.y.z，例如 1.2.0', trigger: 'blur' }
        ],
        platform: [{ required: true, message: '请选择目标平台', trigger: 'change' }],
        arch:     [{ required: true, message: '请选择 CPU 架构', trigger: 'change' }]
      },
      uploading: false,
      uploadedFile: {
        file_name: '',
        file_url: '',
        file_size: 0,
        file_md5: ''
      }
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const params = { ...this.query }
        if (params.platform === '') delete params.platform
        if (params.arch === '') delete params.arch
        if (params.status === '') delete params.status
        const res = await getAppVersionList(params)
        const data = res.data || {}
        this.tableData = data.list || []
        this.total = data.total || 0
      } finally {
        this.loading = false
      }
    },

    handleSearch() {
      this.query.page = 1
      this.fetchData()
    },

    handleReset() {
      this.query = { platform: '', arch: '', status: '', page: 1, pageSize: 15 }
      this.fetchData()
    },

    handleSizeChange(val) {
      this.query.pageSize = val
      this.query.page = 1
      this.fetchData()
    },

    handleCurrentChange(val) {
      this.query.page = val
      this.fetchData()
    },

    handleCreate() {
      this.form = defaultForm()
      this.uploadedFile = { file_name: '', file_url: '', file_size: 0, file_md5: '' }
      this.dialog.title = '发布新版本'
      this.dialog.visible = true
    },

    handleEdit(row) {
      this.form = {
        id:           row.id,
        version:      row.version,
        platform:     row.platform,
        arch:         row.arch,
        file_url:     row.file_url,
        release_note: row.release_note || '',
        is_force:     row.is_force,
        status:       row.status
      }
      this.dialog.title = '编辑版本信息'
      this.dialog.visible = true
    },

    async handleDelete(row) {
      try {
        await this.$confirm(
          `确定删除版本 v${row.version}（${this.platformLabel(row.platform)} / ${row.arch}）吗？删除后不可恢复。`,
          '删除确认',
          { type: 'warning', confirmButtonText: '确定删除', cancelButtonText: '取消' }
        )
        await deleteAppVersion({ id: row.id })
        this.$message.success('删除成功')
        this.fetchData()
      } catch {}
    },

    async handleSubmit() {
      try {
        await this.$refs.versionForm.validate()
        this.dialog.loading = true

        if (this.form.id) {
          await updateAppVersion({
            id:           this.form.id,
            version:      this.form.version,
            release_note: this.form.release_note,
            is_force:     this.form.is_force,
            status:       this.form.status
          })
          this.$message.success('更新成功')
        } else {
          await createAppVersion({
            version:      this.form.version,
            platform:     this.form.platform,
            arch:         this.form.arch,
            file_name:    this.uploadedFile.file_name,
            file_url:     this.uploadedFile.file_url,
            file_size:    this.uploadedFile.file_size,
            file_md5:     this.uploadedFile.file_md5,
            release_note: this.form.release_note,
            is_force:     this.form.is_force,
            status:       this.form.status
          })
          this.$message.success('发布成功')
        }

        this.dialog.visible = false
        this.fetchData()
      } catch {} finally {
        this.dialog.loading = false
      }
    },

    beforeUpload(file) {
      const MAX_SIZE = 2 * 1024 * 1024 * 1024
      if (file.size > MAX_SIZE) {
        this.$message.error('文件大小不能超过 2GB')
        return false
      }
      return true
    },

    async handleFileUpload({ file }) {
      this.uploading = true
      try {
        const res = await uploadAppVersionFile(file)
        this.uploadedFile = res.data || {}
        this.form.file_url = this.uploadedFile.file_url
        this.$refs.versionForm && this.$refs.versionForm.validateField('file_url')
        this.$message.success('文件上传成功')
      } catch (err) {
        this.$message.error(err.message || '文件上传失败')
      } finally {
        this.uploading = false
      }
    },

    clearUpload() {
      this.uploadedFile = { file_name: '', file_url: '', file_size: 0, file_md5: '' }
      this.form.file_url = ''
    },

    resetDialog() {
      this.$refs.versionForm && this.$refs.versionForm.resetFields()
      this.form = defaultForm()
      this.uploadedFile = { file_name: '', file_url: '', file_size: 0, file_md5: '' }
      this.uploading = false
    },

    formatSize(bytes) {
      if (!bytes) return '-'
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
      return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB'
    },

    platformTagType(platform) {
      return { win: 'primary', mac: 'success', linux: 'warning' }[platform] || 'info'
    },

    platformLabel(platform) {
      return { win: 'Windows', mac: 'macOS', linux: 'Linux' }[platform] || platform
    }
  }
}
</script>

<style scoped>
.page-container { padding: 16px; }

.search-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
}

.action-bar {
  margin-bottom: 12px;
}

.version-text {
  font-weight: 600;
  color: #303133;
}

.size-text {
  color: #606266;
  font-size: 13px;
}

.md5-short {
  font-family: monospace;
  font-size: 12px;
  color: #909399;
  cursor: default;
}

.upload-area {
  width: 100%;
}

.upload-progress {
  margin-top: 8px;
  color: #409eff;
  font-size: 13px;
}

.file-info-card {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 10px 14px;
  background: #f9fafb;
}

.file-info-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.file-ok-icon {
  color: #67c23a;
  font-size: 18px;
}

.file-name {
  flex: 1;
  font-size: 13px;
  color: #303133;
  word-break: break-all;
}

.re-upload-btn {
  flex-shrink: 0;
  color: #f56c6c;
}

.file-meta {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
  word-break: break-all;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  line-height: 1.4;
}
</style>
